import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@/i18n';

function detectLangFromPath(pathname: string): SupportedLanguage | null {
  const seg = pathname.split('/').filter(Boolean)[0];
  return isSupportedLanguage(seg) ? seg : null;
}

function buildPath(targetLang: SupportedLanguage, currentPath: string): string {
  const segments = currentPath.split('/').filter(Boolean);
  if (segments.length === 0) return `/${targetLang}/`;
  if (isSupportedLanguage(segments[0])) {
    segments[0] = targetLang;
  } else {
    segments.unshift(targetLang);
  }
  const search = window.location.search;
  return `/${segments.join('/')}${search}`;
}

export function useLocale() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = useMemo<SupportedLanguage>(() => {
    return detectLangFromPath(location.pathname) ?? (i18n.language as SupportedLanguage) ?? DEFAULT_LANGUAGE;
  }, [location.pathname, i18n.language]);

  useEffect(() => {
    if (i18n.language !== currentLang) {
      void i18n.changeLanguage(currentLang);
    }
    document.documentElement.lang = currentLang;
  }, [currentLang, i18n]);

  const setLang = (next: SupportedLanguage) => {
    if (!isSupportedLanguage(next)) return;
    try {
      localStorage.setItem('gtfs-lang', next);
    } catch {
      // ignore
    }
    const target = buildPath(next, location.pathname);
    navigate(target);
  };

  return {
    lang: currentLang,
    setLang,
    supported: SUPPORTED_LANGUAGES,
  };
}
